import { FC, memo, useCallback, useEffect, useState } from 'react';
import {
  Button,
  Flex,
  message,
  Row,
  Space,
  TimePicker,
  Typography
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { usePermission } from 'hooks';
import { updateDoctor } from 'services/api/doctors';

import { useDoctorProfile } from 'pages/DoctorProfile/DoctorProfileContext';
import { WeekDay } from './styled';
import { DayStateType } from './types';
import { IconButton } from 'components/ui';
import { parseJson } from 'utils/json';
import { showErrorMessage } from 'utils/showErrorMessage';
import { Private } from 'components/shared';

const DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday'
];

export const initialWorkingHours = {
  sunday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  monday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  tuesday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  wednesday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  thursday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  friday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  saturday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  }
};

const { Title } = Typography;

const WorkingHours: FC = () => {
  // translations
  const { t } = useTranslation();
  // states
  const [data, setData] = useState<DayStateType>(initialWorkingHours);
  const { checkUserPermission } = usePermission();
  const { profile, refresh } = useDoctorProfile();

  useEffect(() => {
    // BE returns working_hours as an arbitrary jsonb blob — for a fresh
    // doctor this is often `null`, `{}`, or a partial map missing some
    // days / missing the `hours` array. Merge on top of the full
    // initialWorkingHours skeleton so every DAY key + `hours` array
    // exists before render reads `data[item].enabled` / `.hours.map(...)`.
    const workingHours = parseJson(profile?.working_hours);

    setData(prev => {
      const next = { ...prev } as DayStateType;
      DAYS.forEach(day => {
        const src = (workingHours as DayStateType | null | undefined)?.[day];

        if (src && Array.isArray(src.hours) && src.hours.length) {
          next[day] = { enabled: !!src.enabled, hours: src.hours };
        } else {
          next[day] =
            initialWorkingHours[day as keyof typeof initialWorkingHours];
        }
      });

      return next;
    });
  }, [profile?.working_hours]);

  const doctorProfileId = profile?.id;

  const hasUpdatePermission = checkUserPermission(
    PERMISSION_ROUTES.doctors,
    PERMISSION_ACTIONS.update
  );

  const onTimeSelect = useCallback(
    (day: string, i: number, type: 'start' | 'end', value: Dayjs | null) => {
      if (!value || !hasUpdatePermission) return;

      setData(prev => {
        const current = prev[day] || {};

        const hours = [...current.hours];
        hours[i] = {
          ...hours[i],
          [type]: value.format('HH:mm')
        };

        return {
          ...prev,
          [day]: {
            ...current,
            hours
          }
        };
      });
    },
    [hasUpdatePermission]
  );

  const onSelectDay = useCallback(
    (day: string, value: boolean) => {
      if (!hasUpdatePermission) return;

      setData(prev => {
        return {
          ...prev,
          [day]: {
            enabled: value,
            hours: [
              {
                start: '00:00',
                end: '23:30'
              }
            ]
          }
        };
      });
    },
    [hasUpdatePermission]
  );

  const addHour = useCallback((day: string) => {
    setData(prev => {
      const current = prev[day] || {};
      const prevEnd =
        +current.hours[current.hours.length - 1].end.split(':')[0] + 1;

      const start = `${prevEnd > 0 ? prevEnd : `${prevEnd}:00`}:00`;

      return {
        ...prev,
        [day]: {
          ...current,
          hours: [
            ...current.hours,
            {
              start,
              end: '23:30'
            }
          ]
        }
      };
    });
  }, []);

  const deleteHour = useCallback((day: string, i: number) => {
    setData(prev => {
      const current = prev[day] || {};
      const hours = current.hours.filter((_, index) => index !== i);

      return {
        ...prev,
        [day]: {
          ...current,
          hours
        }
      };
    });
  }, []);

  const getDisabledStartTime = useCallback(
    (day: string, index: number) => {
      if (index < 1) return {};

      const prevEndHour = +data[day].hours[index - 1].end.split(':')[0];
      const disabledHours: number[] = [];

      for (let i = 0; i <= prevEndHour; i++) {
        disabledHours.push(i);
      }

      return {
        disabledHours: () => disabledHours
      };
    },
    [data]
  );

  const getDisabledEndTime = useCallback(
    (day: string, index: number) => {
      const startHour = +data[day].hours[index].start.split(':')[0];

      const disabledHours: number[] = [];

      for (let i = 0; i <= startHour; i++) {
        disabledHours.push(i);
      }

      return {
        disabledHours: () => disabledHours
      };
    },
    [data]
  );

  const handleSave = useCallback(async () => {
    if (!doctorProfileId) return;

    try {
      await updateDoctor(doctorProfileId, { working_hours: data });
      refresh();
      message.success(t('messages.successfully_saved'));
    } catch (err) {
      showErrorMessage(err);
    }
  }, [data, doctorProfileId, refresh, t]);

  return (
    <div>
      <Space size={16} direction="vertical" style={{ width: '100%' }}>
        <Row justify="space-between">
          <Space size={12}>
            {DAYS.map(item => {
              const isSelected = data[item].enabled;

              return (
                <WeekDay
                  onClick={() => onSelectDay(item, !isSelected)}
                  isSelected={isSelected}
                  key={`day-button-${item}`}
                  disabled={!hasUpdatePermission}
                >
                  {t(`dates.${item[0]}`)}
                </WeekDay>
              );
            })}
          </Space>
        </Row>
        {DAYS.map(item => {
          const isSelected = data[item].enabled;
          if (!isSelected) return null;

          return (
            <div key={`day-item-${item}`}>
              <Title level={4}>{t(`dates.${item}`)}</Title>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {data[item].hours.map((hour, i, arr) => {
                  const endHour = +hour.end.split(':')[0];

                  return (
                    <Space key={`day-item-hour-${item}-${i}`}>
                      <TimePicker
                        onChange={e => onTimeSelect(item, i, 'start', e)}
                        value={dayjs(hour.start, 'HH:mm')}
                        format="HH:mm"
                        minuteStep={30}
                        showNow={false}
                        inputReadOnly
                        disabledTime={() => getDisabledStartTime(item, i)}
                        disabled={!hasUpdatePermission}
                      />
                      <TimePicker
                        onChange={e => onTimeSelect(item, i, 'end', e)}
                        value={dayjs(hour.end, 'HH:mm')}
                        format="HH:mm"
                        minuteStep={30}
                        showNow={false}
                        inputReadOnly
                        disabledTime={() => getDisabledEndTime(item, i)}
                        disabled={!hasUpdatePermission}
                      />
                      <Private
                        route={PERMISSION_ROUTES.doctors}
                        action={PERMISSION_ACTIONS.update}
                      >
                        {i === arr.length - 1 && endHour < 23 && (
                          <IconButton onClick={() => addHour(item)}>
                            <PlusOutlined />
                          </IconButton>
                        )}
                        {i > 0 && (
                          <IconButton onClick={() => deleteHour(item, i)}>
                            <MinusOutlined />
                          </IconButton>
                        )}
                      </Private>
                    </Space>
                  );
                })}
              </Space>
            </div>
          );
        })}
      </Space>
      <Private
        route={PERMISSION_ROUTES.doctors}
        action={PERMISSION_ACTIONS.update}
      >
        <Flex justify="flex-end" style={{ paddingTop: 24 }}>
          <Button size="large" type="primary" onClick={handleSave}>
            {t('common.save')}
          </Button>
        </Flex>
      </Private>
    </div>
  );
};

export default memo(WorkingHours);
